<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t('pages.campaigns.driverList.modal.new.title') }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <Multiselect
              v-model="rawCampaignRegistration.campaign"
              :error="errors?.campaign"
              class="col-start-1 col-end-2"
              :options="fetchCampaigns"
              :label="$t('campaignRegistration.campaign')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
            />
            <Multiselect
              v-model="rawCampaignRegistration.driver"
              :error="errors?.driver"
              class="col-start-1 col-end-2"
              :options="driverList"
              :label="$t('campaignRegistration.driver')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              :minChars="2"
              :filterResults="false"
              :delay="500"
              :resolveOnLoad="true"
              :createOption="false"
              :clearOnSelect="true"
              :noOptionsText="$t('inputSearchString')"
              :disabled="campaignNeedsChoosing"
            />
            <p>{{ $t('pages.campaigns.driverList.modal.new.hint') }}</p>
          </div>
          <div class="w-full text-center">
            <custom-button
              variant="primary"
              class="mt-15"
              :busy="loading"
              @click="save"
            >
              {{ $t('save') }}
            </custom-button>
          </div>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {computed, reactive, ref, unref} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import CustomButton from '@utils/CustomButton.vue';
import {collOptionsProvider, type ProviderParams } from '@utils/table/hooks/provider';
import type {
  Campaign,
  CampaignGroup, Driver,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const notify = useNotify();

const groupId = computed<{ group?: string }>(() => (props.campaignGroup?.id ? { group: props.campaignGroup.id } : {}));

const params = reactive({
  filter: groupId,
});

const fetchCampaigns = collOptionsProvider(
  '/campaigns',
  params,
  (value: Campaign) => ({
    label: value.name,
    value: value.id,
  }),
);

const initialData = () => (
  {
    campaign: undefined,
    driver: undefined,
  });

const rawCampaignRegistration = ref({
  ...initialData(),
});

const campaignNeedsChoosing = computed(() => !rawCampaignRegistration.value.campaign);

const driverFilter = computed<{ eligibleForCampaign?: string }>(() => (
  rawCampaignRegistration.value?.campaign ? { eligibleForCampaign: rawCampaignRegistration.value.campaign } : {}
));

// const driverParams = reactive({
//   filter: driverFilter,
// });

// const { data: fetchDrivers, loading: driverLoading } = useProvider<HydraCollection<Driver>>(
//   '/drivers',
//   driverParams,
//   { disabled: campaignNeedsChoosing },
// );
//
// const driverList = computed(() => (!driverLoading.value ? unref(fetchDrivers)?.['hydra:member'].map((value: Driver) => ({
//   label: value.user?.fullName,
//   value: value['@id'],
// })) : []));

const driverList = (query: string, select: any) => {
  const input = query || select.modelValue;
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'user.fullName': input, ...driverFilter.value } : { id: select.modelValue },
  };
  return collOptionsProvider<Driver>('/drivers', filterQuery, (value) => ({
    label: value.user?.fullName,
    value: value['@id'],
  }))();
};

const {
  data: campaignRegistration,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    campaign: string().nullable().required(t('validation.notEmpty')),
    driver: string().nullable().required(t('validation.notEmpty')),
  }),
  rawCampaignRegistration,
);

function resetData() {
  rawCampaignRegistration.value = { ...initialData() } as unknown as typeof campaignRegistration['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec('/campaign_registrations', {
      method: 'POST',
      body: {
        campaign: `/campaigns/${campaignRegistration.value.campaign}`,
        driver: campaignRegistration.value.driver,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.campaigns.driverList.modal.new.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.campaigns.driverList.modal.new.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
